import React, { useState, useEffect } from 'react';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from '@/components/ui/sheet';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { Loader2, Wand2 } from 'lucide-react';
import api from '@/lib/api';
import { toast } from 'sonner';

/**
 * BulkEditPanel — apply one field=value to N selected activities.
 *
 * Field allow-list mirrors the backend's ActivityBulkUpdateRequest schema
 * (calendar_id, original_duration). Adding fields here requires a matching
 * change in backend/domains/activities/routes.py and schemas.py.
 *
 * Captures per-activity old values before the PATCH so the parent can push
 * a single grouped entry onto the undo stack.
 */
export default function BulkEditPanel({
  open,
  onOpenChange,
  selectedActivities,
  scheduleId,
  projectCalendars,
  onApplied,
}) {
  const [field, setField] = useState('calendar_id');
  const [calendarValue, setCalendarValue] = useState('__none__');
  const [durationValue, setDurationValue] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const selectionCount = selectedActivities.length;

  // Reset value editors when the panel opens, so a previous edit's value
  // doesn't bleed into the next session.
  useEffect(() => {
    if (open) {
      setField('calendar_id');
      setCalendarValue('__none__');
      setDurationValue('');
    }
  }, [open]);

  const isApplyDisabled = (() => {
    if (submitting || selectionCount < 1) return true;
    if (field === 'calendar_id') return false; // null is a legal value
    if (field === 'original_duration') {
      const n = Number(durationValue);
      return durationValue === '' || !Number.isInteger(n) || n < 0;
    }
    return true;
  })();

  const handleApply = async () => {
    setSubmitting(true);
    let value;
    let displayName;
    if (field === 'calendar_id') {
      value = calendarValue === '__none__' ? null : calendarValue;
      displayName = 'Calendar';
    } else {
      value = Number(durationValue);
      displayName = 'Duration';
    }

    // Snapshot old values BEFORE the PATCH so the undo entry can revert
    // each row to its prior value. Order matches selectedActivities so it
    // round-trips with what the backend response.edits will report.
    const localOldValues = selectedActivities.map(a => ({
      activity_id: a.id,
      old_value: a[field] ?? null,
    }));

    try {
      const res = await api.patch('/activities/bulk', {
        activity_ids: selectedActivities.map(a => a.id),
        field,
        value,
        comment: `Bulk edit: ${displayName} for ${selectionCount} activities`,
      });
      // Prefer server-reported edits (more reliable than local snapshot for
      // calendar_id derivatives etc.); fall back to local on any anomaly.
      const edits = Array.isArray(res.data?.edits) && res.data.edits.length === selectionCount
        ? res.data.edits
        : localOldValues;

      onApplied?.({
        field,
        displayName,
        newValue: value,
        edits,
        scheduleId,
      });
      toast.success(`Applied ${displayName} to ${selectionCount} activities`);
      onOpenChange(false);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'Bulk update failed');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="w-[380px] sm:max-w-[380px]">
        <SheetHeader>
          <SheetTitle className="flex items-center gap-2">
            <Wand2 className="h-4 w-4 text-blue-600" />
            Bulk edit
          </SheetTitle>
          <SheetDescription className="text-xs">
            Apply one value to <strong>{selectionCount}</strong> selected{' '}
            {selectionCount === 1 ? 'activity' : 'activities'}.
          </SheetDescription>
        </SheetHeader>

        <div className="py-4 space-y-4">
          <div className="space-y-1.5">
            <Label htmlFor="bulk-field" className="text-xs font-medium">Field</Label>
            <Select value={field} onValueChange={setField}>
              <SelectTrigger id="bulk-field" className="h-8 text-xs" data-testid="bulk-field-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="calendar_id">Calendar</SelectItem>
                <SelectItem value="original_duration">Duration (days)</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {field === 'calendar_id' && (
            <div className="space-y-1.5">
              <Label htmlFor="bulk-calendar" className="text-xs font-medium">Calendar</Label>
              <Select value={calendarValue} onValueChange={setCalendarValue}>
                <SelectTrigger id="bulk-calendar" className="h-8 text-xs" data-testid="bulk-calendar-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none__">— Use project default —</SelectItem>
                  {(projectCalendars || []).map(cal => (
                    <SelectItem key={cal.id} value={cal.id}>{cal.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-[10px] text-slate-400">
                Each activity's duration is recomputed from its existing dates on the new calendar.
              </p>
            </div>
          )}

          {field === 'original_duration' && (
            <div className="space-y-1.5">
              <Label htmlFor="bulk-duration" className="text-xs font-medium">Duration (days)</Label>
              <Input
                id="bulk-duration"
                type="number"
                min={0}
                step={1}
                value={durationValue}
                onChange={(e) => setDurationValue(e.target.value)}
                className="h-8 text-xs"
                placeholder="e.g. 5"
                data-testid="bulk-duration-input"
              />
              <p className="text-[10px] text-slate-400">
                Each activity's planned finish is recalculated from its start using its calendar.
              </p>
            </div>
          )}
        </div>

        <div className="flex items-center justify-end gap-2 pt-2 border-t">
          <Button variant="outline" size="sm" onClick={() => onOpenChange(false)} disabled={submitting}>
            Cancel
          </Button>
          <Button
            size="sm"
            className="bg-blue-600 hover:bg-blue-700 text-white"
            disabled={isApplyDisabled}
            onClick={handleApply}
            data-testid="bulk-apply-button"
          >
            {submitting ? (
              <><Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" /> Applying…</>
            ) : (
              `Apply to ${selectionCount}`
            )}
          </Button>
        </div>
      </SheetContent>
    </Sheet>
  );
}
